<script setup lang="ts">
import SectionHeading from '@/components/SectionHeading.vue';

// п. 4.3.3 — стандартные условия договоров лизинга
const terms = [
  {
    title: 'Срок владения и пользования предметом лизинга',
    text: 'От 12 до 72 месяцев. Конкретный срок определяется договором лизинга и графиком платежей.',
  },
  {
    title: 'Порядок определения размера лизинговых платежей и вознаграждения (дохода) лизингодателя',
    text: 'Размер лизинговых платежей определяется договором лизинга и графиком платежей; вознаграждение (доход) лизингодателя включается в состав лизингового платежа.',
  },
  {
    title: 'Размер авансового платежа',
    text: 'От 0% до 40% стоимости предмета лизинга; для физических лиц — от 10% до 40%.',
  },
  {
    title: 'Способы обеспечения исполнения обязательств',
    text: 'Неустойка, поручительство, залог, задаток, гарантийный депозит, страхование предмета лизинга.',
  },
  {
    title: 'Необходимость заключения иных договоров и обязанная сторона',
    text: 'Договор купли-продажи предмета лизинга заключает лизингодатель; договор страхования предмета лизинга — лизингополучатель; договоры залога и поручительства — лизингополучатель или третье лицо.',
  },
  {
    title: 'Размер выкупной стоимости (при выкупе предмета лизинга)',
    text: 'От 0,1% стоимости предмета лизинга. Порядок выкупа определяется договором лизинга.',
  },
  {
    title: 'Валюта денежного обязательства и порядок расчёта суммы очередного платежа',
    text: 'BYN, а также USD, EUR, RUB — для юридических лиц и индивидуальных предпринимателей. Сумма очередного платежа рассчитывается в соответствии с договором лизинга и графиком платежей.',
  },
  {
    title: 'Периодичность уплаты платежей, разовые платежи и форма расчётов',
    text: 'Платежи уплачиваются ежемесячно либо с иной периодичностью, установленной договором; возможны авансовые и разовые платежи. Расчёты производятся в безналичном порядке.',
  },
];
</script>

<template>
  <!-- п. 4.3.1. Контрагенты, предметы и виды лизинга -->
  <section>
    <div class="container-narrow">
      <SectionHeading
        title="Контрагенты, предметы и виды лизинга"
        lead="Сведения о лизингополучателях, предметах лизинга и применяемых видах лизинга."
      />
      <div class="grid grid-cols-3">
        <div class="card">
          <h4 class="card__title-md">Контрагенты</h4>
          <p class="card__text">Физические лица; юридические лица и индивидуальные предприниматели.</p>
        </div>
        <div class="card">
          <h4 class="card__title-md">Предметы лизинга</h4>
          <p class="card__text">Транспорт (легковые автомобили, коммерческий транспорт), специальная техника, оборудование.</p>
        </div>
        <div class="card">
          <h4 class="card__title-md">Виды лизинга</h4>
          <p class="card__text">Финансовый лизинг.</p>
        </div>
      </div>
    </div>
  </section>

  <!-- п. 4.3.2. Документы и сведения для оценки -->
  <section class="section-tinted">
    <div class="container-narrow">
      <SectionHeading
        title="Документы и сведения, истребуемые лизингодателем"
        lead="Лизингодатель истребует документы и сведения для оценки финансовой устойчивости и деловой репутации лизингополучателя, ликвидности предмета лизинга и инвестиционного проекта."
      />
      <ul role="list" class="check-list">
        <li class="check-list__item">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--color-success)" stroke-width="2"><circle cx="12" cy="12" r="10" /><path d="M8 12l3 3 5-6" /></svg>
          <span><strong>Финансовая устойчивость:</strong> бухгалтерская (финансовая) отчётность, справки о доходах и движении средств по счетам, сведения о действующих обязательствах.</span>
        </li>
        <li class="check-list__item">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--color-success)" stroke-width="2"><circle cx="12" cy="12" r="10" /><path d="M8 12l3 3 5-6" /></svg>
          <span><strong>Деловая репутация:</strong> учредительные документы, сведения о руководителе и участниках, сведения из кредитного регистра.</span>
        </li>
        <li class="check-list__item">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--color-success)" stroke-width="2"><circle cx="12" cy="12" r="10" /><path d="M8 12l3 3 5-6" /></svg>
          <span><strong>Ликвидность предмета лизинга:</strong> сведения о предмете лизинга, его состоянии, годе выпуска, комплектации и рыночной стоимости.</span>
        </li>
        <li class="check-list__item">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--color-success)" stroke-width="2"><circle cx="12" cy="12" r="10" /><path d="M8 12l3 3 5-6" /></svg>
          <span><strong>Инвестиционный проект:</strong> описание целей приобретения предмета лизинга, планируемых источников и сроков исполнения обязательств.</span>
        </li>
      </ul>
      <p class="field-hint check-list__note">
        Полный перечень документов приведён на странице
        <a href="/requirements.html">«Документы и требования»</a>.
      </p>
    </div>
  </section>

  <!-- п. 4.3.3. Стандартные условия договора лизинга -->
  <section>
    <div class="container-narrow">
      <SectionHeading
        title="Стандартные условия договора лизинга"
        lead="Стандартные условия, применяемые лизингодателем при заключении договоров лизинга."
      />
      <div class="grid grid-cols-2">
        <div v-for="term in terms" :key="term.title" class="card">
          <h4 class="card__title-md">{{ term.title }}</h4>
          <p class="card__text">{{ term.text }}</p>
        </div>
      </div>
      <p class="field-hint docs-note">
        <strong>Условия договора лизинга могут определяться по согласованию сторон.</strong>
      </p>
    </div>
  </section>
</template>
